<template>
  <div class="wc-week fill-height d-flex flex-column" style="background: var(--bg-main)">
    <!-- Week Header -->
    <div class="wc-week-header" :class="{ 'flex-column': $vuetify.breakpoint.mobile }">
      <div class="d-flex align-center" style="gap: 12px">
        <v-btn icon x-large @click="$emit('back')" style="color: var(--icon-muted)">
          <v-icon x-large>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="wc-week-title">KW {{ weekNumber }}</div>
          <div class="wc-week-range">{{ weekRangeText }}</div>
        </div>
        <div v-if="isCurrentWeek" class="wc-today-pill">Diese Woche</div>
      </div>

      <div class="d-flex align-center" style="gap: 8px">
        <div class="wc-week-stats">
          <v-icon small style="color: var(--icon-muted)" class="mr-1">mdi-account-group</v-icon>
          <span>{{ employees.length }} MA</span>
        </div>
        <v-btn icon @click="prevWeek" style="color: var(--icon-muted)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          small
          depressed
          @click="goToday"
          style="background: rgba(76, 175, 80, 0.1); color: #66bb6a; text-transform: none; font-weight: 600; border-radius: 6px">
          Heute
        </v-btn>
        <v-btn icon @click="nextWeek" style="color: var(--icon-muted)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          x-small
          depressed
          @click="$emit('switch-view', 'day')"
          style="
            background: rgba(66, 165, 245, 0.1);
            color: #42a5f5;
            text-transform: none;
            font-weight: 600;
            border-radius: 6px;
            border: 1px solid rgba(66, 165, 245, 0.15);
            height: 26px;
            min-width: 0;
            padding: 0 10px;
            font-size: 0.7rem;
          "
          title="Tagesansicht">
          <v-icon small class="mr-1">mdi-view-day</v-icon> Tag
        </v-btn>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex-grow-1 d-flex align-center justify-center">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="40" width="2"></v-progress-circular>
        <div style="color: var(--text-disabled); font-size: 0.8rem; margin-top: 12px">Woche wird geladen...</div>
      </div>
    </div>

    <!-- Grid -->
    <div v-else class="wc-week-grid flex-grow-1 overflow-auto custom-scrollbar">
      <!-- Day Header Row -->
      <div class="wc-week-row wc-week-day-header sticky-top">
        <div class="wc-week-emp-cell wc-week-corner">Mitarbeiter</div>
        <div
          v-for="date in dates"
          :key="'dh-' + date"
          class="wc-week-day-cell"
          :class="{ 'wc-today-col': isToday(date) }"
          @click="$emit('open-day', date)">
          <div class="wc-day-short">{{ formatDayShort(date) }}</div>
          <div class="wc-day-num" :class="{ 'wc-day-today': isToday(date) }">{{ formatDayNum(date) }}</div>
        </div>
      </div>

      <!-- Empty -->
      <div v-if="employees.length === 0" class="d-flex align-center justify-center" style="min-height: 300px">
        <div class="text-center">
          <v-icon size="48" style="color: var(--icon-muted); opacity: 0.5">mdi-calendar-week</v-icon>
          <div style="color: var(--text-disabled); font-size: 0.85rem; margin-top: 12px">Keine Daten für diese Woche</div>
        </div>
      </div>

      <!-- Employee Rows -->
      <div v-for="emp in employees" :key="emp.info._id" class="wc-week-row">
        <!-- Employee Name -->
        <div class="wc-week-emp-cell">
          <v-avatar size="30" class="mr-2" style="border: 2px solid var(--border-medium)">
            <img :src="emp.info.picture ? '/img/profilbilder/' + emp.info.picture : '/img/default-avatar.png'" style="object-fit: cover" />
          </v-avatar>
          <div class="wc-week-emp-name">{{ emp.info.firstName }}</div>
        </div>

        <!-- Day Cells -->
        <div
          v-for="date in dates"
          :key="'c-' + emp.info._id + '-' + date"
          class="wc-week-day-cell wc-week-content-cell"
          :class="{ 'wc-today-col': isToday(date), 'wc-has-data': hasData(emp, date) }"
          @click="$emit('open-day', date)">
          <!-- Shift indicator -->
          <div v-if="getShift(emp, date)" class="wc-cell-shift" :style="{ borderLeftColor: getShift(emp, date).color }">
            <span class="wc-cell-shift-time">{{ getShift(emp, date).start }} - {{ getShift(emp, date).end }}</span>
          </div>

          <!-- Appointments -->
          <div
            v-for="(appt, i) in getAppointments(emp, date).slice(0, 3)"
            :key="'a' + i"
            class="wc-cell-appt"
            :style="{ borderLeftColor: appt.color || '#4CAF50' }">
            <span class="wc-cell-appt-time">{{ appt.start }}</span>
            <span class="wc-cell-appt-title">{{ appt.title }}</span>
          </div>
          <div v-if="getAppointments(emp, date).length > 3" class="wc-cell-more">+{{ getAppointments(emp, date).length - 3 }} mehr</div>

          <!-- Tasks -->
          <div v-if="getTasks(emp, date).length > 0" class="wc-cell-tasks">
            <v-icon x-small style="font-size: 10px; color: #ab47bc">mdi-checkbox-marked</v-icon>
            <span>{{ getTasks(emp, date).length }}</span>
          </div>

          <!-- Notes -->
          <div v-if="getNotesCount(emp, date) > 0" class="wc-cell-notes" @click.stop="openNoteFor(emp, date)" title="Notizen ansehen">
            <v-icon x-small style="font-size: 10px; color: #42a5f5">mdi-message-text</v-icon>
            <span>{{ getNotesCount(emp, date) }}</span>
          </div>

          <!-- Hover Actions -->
          <div class="wc-week-cell-actions" v-if="hasData(emp, date) && getNotesCount(emp, date) === 0">
            <v-btn icon x-small @click.stop="openNoteFor(emp, date)" title="Notiz hinzufügen" class="wc-cell-action-btn">
              <v-icon x-small>mdi-message-text-outline</v-icon>
            </v-btn>
          </div>

          <!-- Empty day indicator -->
          <div v-if="!hasData(emp, date)" class="wc-cell-empty">—</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import api from "@/api"

export default {
  name: "WorkCalendarWeek",
  props: {
    initialDate: { type: String, default: () => moment().format("YYYY-MM-DD") },
  },
  data() {
    return {
      loading: false,
      currentStart: moment(this.initialDate).startOf("isoWeek").format("YYYY-MM-DD"),
      dates: [],
      employees: [],
    }
  },
  computed: {
    weekNumber() {
      return moment(this.currentStart).isoWeek()
    },
    weekRangeText() {
      const start = moment(this.currentStart)
      const end = start.clone().add(6, "days")
      return `${start.format("DD. MMM")} – ${end.format("DD. MMM YYYY")}`
    },
    isCurrentWeek() {
      return moment().startOf("isoWeek").format("YYYY-MM-DD") === this.currentStart
    },
  },
  mounted() {
    this.loadWeek()
  },
  methods: {
    async loadWeek() {
      this.loading = true
      try {
        const { data } = await api.shiftManagement.fetchWeeklyView(this.currentStart)
        this.dates = data.dates
        this.employees = data.employees
      } catch (e) {
        console.error("Weekly load error:", e)
        this.$toast && this.$toast.error("Woche konnte nicht geladen werden")
      } finally {
        this.loading = false
      }
    },
    prevWeek() {
      this.currentStart = moment(this.currentStart).subtract(7, "days").format("YYYY-MM-DD")
      this.loadWeek()
    },
    nextWeek() {
      this.currentStart = moment(this.currentStart).add(7, "days").format("YYYY-MM-DD")
      this.loadWeek()
    },
    goToday() {
      this.currentStart = moment().startOf("isoWeek").format("YYYY-MM-DD")
      this.loadWeek()
    },
    isToday(date) {
      return moment().format("YYYY-MM-DD") === date
    },
    formatDayShort(date) {
      return moment(date).format("dd")
    },
    formatDayNum(date) {
      return moment(date).format("DD")
    },
    hasData(emp, date) {
      const day = emp.days[date]
      if (!day) return false
      return day.shifts.length > 0 || day.appointments.length > 0 || day.tasks.length > 0
    },
    getShift(emp, date) {
      const day = emp.days[date]
      if (!day || day.shifts.length === 0) return null
      const s = day.shifts[0]
      const times = s.times || []
      if (times.length === 0) return null
      const startTime = times.reduce((min, t) => (t.start < min ? t.start : min), "23:59")
      const endTime = times.reduce((max, t) => (t.end > max ? t.end : max), "00:00")
      const color = s.shiftTemplate?.color || "#666"
      return { start: startTime, end: endTime, color }
    },
    getAppointments(emp, date) {
      const day = emp.days[date]
      if (!day) return []
      return day.appointments.map((a) => ({
        start: a.start || a.time || "",
        title: a.title || (a.customer ? a.customer.vorname : "Termin"),
        color: a.color || "#4CAF50",
      }))
    },
    getTasks(emp, date) {
      const day = emp.days[date]
      if (!day) return []
      return day.tasks || []
    },
    getNotesCount(emp, date) {
      const day = emp.days[date]
      if (!day) return 0
      return (day.notes || []).length
    },
    openNoteFor(emp, date) {
      // Create a pseudo-column object to satisfy the noteDialog structure
      this.$emit("open-note", {
        info: emp.info,
        date: date,
        notes: [],
      })
    },
  },
}
</script>

<style scoped>
/* ===== HEADER ===== */
.wc-week-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
  flex-shrink: 0;
}
.wc-week-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-main);
  line-height: 1.2;
}
.wc-week-range {
  font-size: 0.8rem;
  color: var(--text-muted);
}
.wc-today-pill {
  font-size: 0.8rem;
  font-weight: 700;
  color: #66bb6a;
  background: rgba(76, 175, 80, 0.12);
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid rgba(76, 175, 80, 0.2);
}
.wc-week-stats {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--icon-muted);
}

/* ===== GRID ===== */
.wc-week-grid {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}
.wc-week-row {
  display: flex;
  flex: 1;
  min-height: 80px;
  border-bottom: 1px solid var(--border-light);
}
.wc-week-row:hover .wc-week-emp-cell {
  background: var(--bg-panel);
}
.wc-week-row:hover .wc-week-content-cell {
  background: var(--bg-panel-hover);
}

/* Day Header Row */
.wc-week-day-header {
  z-index: 10;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
  flex: 0 0 auto !important;
  min-height: 0 !important;
}
.sticky-top {
  position: sticky;
  top: 0;
}

/* Corner Cell */
.wc-week-corner {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-disabled);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Employee Cell */
.wc-week-emp-cell {
  width: 160px;
  min-width: 160px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-right: 1px solid var(--border-light);
  background: var(--bg-card);
}
.wc-week-emp-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Day Cells */
.wc-week-day-cell {
  flex: 1;
  min-width: 120px;
  padding: 8px 6px;
  border-right: 1px solid var(--border-light);
  cursor: pointer;
  transition: background 0.1s;
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: relative;
}
.wc-week-day-cell:hover {
  background: var(--bg-panel-hover);
}
.wc-today-col {
  background: rgba(76, 175, 80, 0.03) !important;
}

/* Cell Actions */
.wc-week-cell-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}
.wc-week-day-cell:hover .wc-week-cell-actions {
  opacity: 1;
}
.wc-cell-action-btn {
  background: var(--bg-panel-dark) !important;
  color: var(--text-main) !important;
}

/* Day Header */
.wc-day-short {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  text-align: center;
}
.wc-day-num {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-disabled);
  text-align: center;
  line-height: 1.4;
}
.wc-day-today {
  color: #66bb6a !important;
  background: rgba(76, 175, 80, 0.15);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* Cell Content */
.wc-cell-shift {
  border-left: 3px solid;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--bg-panel);
  margin-bottom: 2px;
}
.wc-cell-shift-time {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-muted);
}
.wc-cell-appt {
  border-left: 2px solid;
  padding: 2px 5px;
  border-radius: 3px;
  background: var(--bg-panel);
  display: flex;
  align-items: center;
  gap: 4px;
}
.wc-cell-appt-time {
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--text-disabled);
  flex-shrink: 0;
}
.wc-cell-appt-title {
  font-size: 0.65rem;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wc-cell-more {
  font-size: 0.6rem;
  color: var(--text-disabled);
  text-align: center;
  padding: 1px;
}
.wc-cell-tasks {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6rem;
  color: #ab47bc;
  padding: 1px 4px;
}
.wc-cell-notes {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6rem;
  color: #42a5f5;
  padding: 1px 4px;
  cursor: pointer;
  border-radius: 3px;
}
.wc-cell-notes:hover {
  background: rgba(66, 165, 245, 0.1);
}
.wc-cell-empty {
  font-size: 0.65rem;
  color: var(--border-strong);
  text-align: center;
  padding: 4px;
}

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 3px;
}
</style>
